/**
 * saas-mapping.ts — wire↔view-model mapping for the ɳSentry SaaS gateway
 *
 * Purpose:    The gateway (api.sentry.nself.org) speaks the documented /v1
 *             contract: enveloped snake_case ({"monitors":[...]}, fields like
 *             interval_seconds, created_at) — the same shape the CLI consumes.
 *             This module converts those wire payloads into the SPA's
 *             camelCase view models. Pure functions, no fetch — unit-testable
 *             against captured gateway fixtures.
 * Inputs:     Parsed JSON from saas-client.ts requests
 * Outputs:    Monitor / Incident / StatusPage / ApiKey / Overview / BillingInfo
 * Constraints: Tolerant of missing optional fields (older gateway builds);
 *             never throws on absent envelope keys — returns empty defaults.
 * SOT:        plugins-pro/paid/nself-saas-gateway handlers_* (wire contract)
 */
import type { TierId } from './tiers'
import type {
  Monitor,
  MonitorCheck,
  Incident,
  StatusPage,
  StatusPageSubscriber,
  PublicStatusPage,
  PublicStatusComponent,
  ApiKey,
  Overview,
  BillingInfo,
} from './saas-client'

// ─── Wire shapes (gateway contract, snake_case) ──────────────────────────────

export interface WireMonitor {
  id: string
  name: string
  url: string
  kind: string
  interval_seconds: number
  status: 'up' | 'down' | 'paused' | 'pending'
  paused: boolean
  created_at: string
  /** Optional probe telemetry — older gateway builds omit these. */
  latency_ms?: number | null
  last_checked_at?: string | null
  ssl_expires_at?: string | null
}

/** One uptime probe result (GET /v1/monitors/{id}/checks — rolling out). */
export interface WireMonitorCheck {
  checked_at: string
  status: string
  latency_ms?: number | null
}

export interface WireIncident {
  id: string
  monitor_id: string
  title: string
  status: 'open' | 'acknowledged' | 'resolved'
  severity: string
  started_at: string
  acknowledged_at?: string
  resolved_at?: string
}

export interface WireStatusPage {
  id: string
  name: string
  slug: string
  url: string
  public: boolean
  created_at: string
  custom_domain?: string | null
  components?: Array<{ id: string; name: string; status?: string }>
}

export interface WireSubscriber {
  id: string
  email: string
  created_at?: string | null
}

export interface WirePublicStatusComponent {
  id: string
  name: string
  status: string
  uptime_percent: number | null
}

export interface WirePublicStatusPage {
  title: string
  slug: string
  overall_status: string
  components?: WirePublicStatusComponent[]
  incidents?: Array<{ title: string; status: string; started_at: string; resolved_at?: string }>
  generated_at?: string
}

export interface WireApiKey {
  id: string
  name: string
  prefix: string
  scope: 'read' | 'full'
  created_at: string
  last_used_at: string | null
  revoked: boolean
}

export interface WireQuota {
  used: number
  limit: number
}

export interface WireOverview {
  monitors: { total: number; up: number; down: number; paused: number; pending: number }
  incidents: { open: number }
  uptime_percent_24h: number | null
  errors: { events_this_month: number }
  rum: { pageviews_this_month: number }
}

export interface WireBilling {
  tier: string
  quotas: Record<string, WireQuota>
  portal_url: string
}

// ─── Mappers ─────────────────────────────────────────────────────────────────

const KNOWN_SEVERITIES = new Set(['critical', 'major', 'minor'])

/**
 * Gateway tier values → SPA TierId. The gateway says 'bundle'/'plus'
 * (np_saas_tenants.tier); the SPA's tier table keys are
 * 'sentry-bundle'/'nself-plus'. Unknown values fall back to 'free' so a
 * contract drift degrades (quota meters show free limits) instead of
 * crashing TIERS[tier] lookups.
 */
const TIER_ALIASES: Record<string, TierId> = {
  free: 'free',
  bundle: 'sentry-bundle',
  'sentry-bundle': 'sentry-bundle',
  sentry: 'sentry-bundle',
  plus: 'nself-plus',
  'nself-plus': 'nself-plus',
  nself_plus: 'nself-plus',
}

export function mapMonitor(w: WireMonitor): Monitor {
  return {
    id: w.id,
    name: w.name,
    url: w.url,
    intervalSeconds: w.interval_seconds,
    status: w.status,
    // Optional probe telemetry — null until the gateway envelope carries it.
    latencyMs: w.latency_ms ?? null,
    lastCheckedAt: w.last_checked_at ?? null,
    sslExpiresAt: w.ssl_expires_at ?? null,
  }
}

export function mapMonitors(body: { monitors?: WireMonitor[] }): Monitor[] {
  return (body.monitors ?? []).map(mapMonitor)
}

/** Map the per-monitor checks envelope; unknown statuses coerce to 'down'. */
export function mapMonitorChecks(body: { checks?: WireMonitorCheck[] }): MonitorCheck[] {
  return (body.checks ?? []).map((c) => ({
    checkedAt: c.checked_at,
    status: c.status === 'up' ? 'up' : 'down',
    latencyMs: c.latency_ms ?? null,
  }))
}

export function mapIncident(w: WireIncident): Incident {
  return {
    id: w.id,
    title: w.title,
    status: w.status,
    severity: (KNOWN_SEVERITIES.has(w.severity) ? w.severity : 'minor') as Incident['severity'],
    monitorId: w.monitor_id || undefined,
    startedAt: w.started_at,
    resolvedAt: w.resolved_at,
  }
}

export function mapIncidents(body: { incidents?: WireIncident[] }): Incident[] {
  return (body.incidents ?? []).map(mapIncident)
}

const COMPONENT_STATUSES = new Set(['operational', 'degraded', 'down'])

export function mapStatusPage(w: WireStatusPage): StatusPage {
  return {
    id: w.id,
    slug: w.slug,
    title: w.name,
    components: (w.components ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      status: (COMPONENT_STATUSES.has(c.status ?? '') ? c.status : 'operational') as StatusPage['components'][number]['status'],
    })),
    published: w.public,
    customDomain: w.custom_domain ?? null,
  }
}

export function mapStatusPages(body: { status_pages?: WireStatusPage[] }): StatusPage[] {
  return (body.status_pages ?? []).map(mapStatusPage)
}

/**
 * SPA StatusPage patch → gateway PATCH body. The wire speaks snake_case with
 * different key names (title→name, published→public) — sending the camelCase
 * view-model keys would silently no-op on the gateway.
 */
export function statusPagePatchToWire(patch: Partial<StatusPage>): Record<string, unknown> {
  const wire: Record<string, unknown> = {}
  if (patch.title !== undefined) wire.name = patch.title
  if (patch.slug !== undefined) wire.slug = patch.slug
  if (patch.published !== undefined) wire.public = patch.published
  if (patch.customDomain !== undefined) wire.custom_domain = patch.customDomain
  if (patch.components !== undefined) {
    wire.components = patch.components.map((c) => ({ id: c.id, name: c.name, status: c.status }))
  }
  return wire
}

export function mapSubscribers(body: { subscribers?: WireSubscriber[] }): StatusPageSubscriber[] {
  return (body.subscribers ?? []).map((s) => ({
    id: s.id,
    email: s.email,
    createdAt: s.created_at ?? null,
  }))
}

const PUBLIC_COMPONENT_STATUSES = new Set(['operational', 'degraded', 'down', 'unknown'])
const PUBLIC_OVERALL_STATUSES = new Set(['operational', 'degraded', 'down'])

/** Map the public status-page wire envelope ({"status_page":{...}}). */
export function mapPublicStatusPage(body: { status_page?: WirePublicStatusPage }): PublicStatusPage {
  const p = body.status_page
  return {
    title: p?.title ?? '',
    overallStatus: (PUBLIC_OVERALL_STATUSES.has(p?.overall_status ?? '')
      ? p?.overall_status
      : 'operational') as PublicStatusPage['overallStatus'],
    components: (p?.components ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      status: (PUBLIC_COMPONENT_STATUSES.has(c.status)
        ? c.status
        : 'unknown') as PublicStatusComponent['status'],
      uptimePercent: c.uptime_percent ?? null,
    })),
    incidents: (p?.incidents ?? []).map((i) => ({
      title: i.title,
      status: i.status,
      startedAt: i.started_at,
    })),
    generatedAt: p?.generated_at,
  }
}

export function mapApiKey(w: WireApiKey): ApiKey {
  return {
    id: w.id,
    name: w.name,
    prefix: w.prefix,
    scope: w.scope,
    createdAt: w.created_at,
    lastUsedAt: w.last_used_at ?? null,
    revoked: w.revoked ?? false,
  }
}

export function mapApiKeys(body: { api_keys?: WireApiKey[] }): ApiKey[] {
  return (body.api_keys ?? []).map(mapApiKey)
}

export function mapOverview(body: { overview?: WireOverview }): Overview {
  const o = body.overview
  return {
    monitors: {
      total: o?.monitors.total ?? 0,
      up: o?.monitors.up ?? 0,
      down: o?.monitors.down ?? 0,
      paused: o?.monitors.paused ?? 0,
      pending: o?.monitors.pending ?? 0,
    },
    incidentsOpen: o?.incidents.open ?? 0,
    uptimePct24h: o?.uptime_percent_24h ?? null,
    errors: {
      eventsThisMonth: o?.errors.events_this_month ?? 0,
      // Error grouping is not part of the gateway overview yet.
      topGroups: [],
    },
    rum: {
      pageviewsThisMonth: o?.rum.pageviews_this_month ?? 0,
      // Web-vitals percentiles arrive with the RUM plugin bridge.
      p75LcpMs: 0,
      p75InpMs: 0,
      errorRatePct: 0,
    },
  }
}

/**
 * GET /v1/usage — standalone usage/quota envelope (same per-dimension
 * {used, limit} records as billing.quotas). Tolerant of absent dimensions
 * so a partial rollout degrades to zeros instead of crashing.
 */
export function mapUsage(body: { usage?: Record<string, WireQuota> }): BillingInfo['usage'] {
  const q = (dim: string): WireQuota => body.usage?.[dim] ?? { used: 0, limit: 0 }
  return {
    monitors: q('monitors').used,
    statusPages: q('status_pages').used,
    errorEventsThisMonth: q('error_events_month').used,
    rumPageviewsThisMonth: q('rum_pageviews_month').used,
    heartbeats: q('heartbeats').used,
  }
}

export function mapBilling(body: { billing?: WireBilling }): BillingInfo {
  const b = body.billing
  const q = (dim: string): WireQuota => b?.quotas?.[dim] ?? { used: 0, limit: 0 }
  return {
    tier: TIER_ALIASES[b?.tier ?? ''] ?? 'free',
    usage: {
      monitors: q('monitors').used,
      statusPages: q('status_pages').used,
      errorEventsThisMonth: q('error_events_month').used,
      rumPageviewsThisMonth: q('rum_pageviews_month').used,
      heartbeats: q('heartbeats').used,
    },
    portalUrl: b?.portal_url,
  }
}

/** Derive the gateway's monitor `kind` from a URL (https default). */
export function monitorKindFromUrl(url: string): string {
  return url.startsWith('http://') ? 'http' : 'https'
}
